import React from 'react';
import {
    TextField,
    Grid,
    InputAdornment,
    FormControlLabel,
    Checkbox,
    Link,
    Button,
    CircularProgress,
    Box,
    Typography,
    Alert,
    Autocomplete,
    Chip,
    IconButton,
    Tooltip,
    Divider,
} from "@mui/material";

import LockIcon from "@mui/icons-material/Lock";
import CheckIcon from "@mui/icons-material/Check";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DmsTextField, DmsTextFieldEmail, DmsTextFieldPhone, intl } from "dmsbasereact";
import { DmsTextFieldCpf } from "../../../components/DmsTextFieldCpf/DmsTextFieldCpf";
import { DmsTextFieldCep } from "../../../components/DmsTextFieldCep/DmsTextFieldCep";
import { DmsAutocomplete } from "../../../components/DmsAutoComplete/DmsAutoComplete";
import { PasswordValidation } from "../../../components/PasswordValidation/PasswordValidation";
import {
    includesPrescriber,
    includesDispenser,
    includesDistributorResponsible,
    includesInstitutionResponsible,
    isForbiddenCombination,
    USER_TYPE_VALUES
} from '../../../constants/UserType';
import { InstitutionCategoryEnum } from '../../../constants/InstitutionType';
import { DmsInstitutionSelect } from '../../../components/DmsInstitutionSelect/DmsInstitutionSelect';
import { TextFieldCrm } from '../../../components/TextFieldCRM/TextFieldCRM';
import { TextFieldCrf } from '../../../components/TextFieldCRF/TextFieldCRF';
import { InstitutionSetupModal } from '../../../components/InstitutionSetupModal';

export const CreateAccountForm = ({
    state,
    handlers,
    stateOptions,
    userTypeOptions
}) => {
    const {
        name,
        email,
        phone,
        password,
        confirmPassword,
        userTypes,
        selectedInstitutions,
        responsibleForInstitutions,
        institutionNamesMap,
        showResponsibleForField,
        cpf,
        crm,
        crf,
        legalDocumentsAccepted,
        institutionLinkDeclaration,
        isSubmitting,
        fieldErrors,
        isValidatingCrm,
        isCrmValid,
        crmValidationWarning,
        crmValidationError,
        canValidateCrm,
        zipCode,
        city,
        addressState,
        address,
        addressNumber,
        crmState,
        crfState,
        institutionModalVisible,
        createdInstitutions,
    } = state;

    const {
        setPhone,
        setPassword,
        setConfirmPassword,
        onChangeUserTypes,
        setSelectedInstitutions,
        setResponsibleForInstitutions,
        setCpf,
        setCpfIsValid,
        setCrf,
        setCrfState,
        setLegalDocumentsAccepted,
        setInstitutionLinkDeclaration,
        handleOpenDocument,
        handleSubmit,
        onChangeName,
        onChangeCrm,
        onChangeCrmState,
        handleCrmValidation,
        setZipCode,
        setCity,
        setAddressState,
        setAddress,
        setAddressNumber,
        setInstitutionModalVisible,
        handleInstitutionSave,
        handleInstitutionsLoaded,
    } = handlers;

    // Determine which type-specific fields to show based on selected types
    const showPrescriberFields = includesPrescriber(userTypes);
    const showDispenserFields = includesDispenser(userTypes);

    // Determine institution category filter
    // If distributor responsible is selected, only show distributors (category 100)
    const institutionCategoryFilter = includesDistributorResponsible(userTypes)
        ? InstitutionCategoryEnum.Distributor
        : null;

    // Get selected type options for the autocomplete
    const selectedTypeOptions = userTypeOptions.filter(opt => userTypes?.includes(opt.value));

    // Check if the exclusive type DISTRIBUTOR_RESPONSIBLE is selected
    const hasDistributorResponsible = includesDistributorResponsible(userTypes);

    // User types info tooltip content
    const userTypesInfoTooltip = (
        <Box sx={{ p: 1, maxWidth: 400 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {intl.get("UserTypes.Info.Title").d("User Types")}
            </Typography>

            <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.main" }}>
                    {intl.get("Prescriber").d("Prescriber")}:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {intl.get("UserTypes.Info.Prescriber").d(
                        "Physician with active registration in the Regional Medical Council (CRM), responsible for prescribing Nuvyor (lenalidomide)."
                    )}
                </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.main" }}>
                    {intl.get("Dispenser").d("Dispenser")}:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {intl.get("UserTypes.Info.Dispenser").d(
                        "Pharmacist with active registration in the Regional Pharmacy Council (CRF), responsible for dispensing Nuvyor (lenalidomide)."
                    )}
                </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.main" }}>
                    {intl.get("InstitutionResponsible").d("Institution Responsible")}:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {intl.get("UserTypes.Info.InstitutionResponsible").d(
                        "Professional, pharmacist or not, responsible for managing drug inventory between units of the same economic group."
                    )}
                </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.main" }}>
                    {intl.get("DistributorResponsible").d("Distributor Responsible")}:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {intl.get("UserTypes.Info.DistributorResponsible").d(
                        "Professional, pharmacist or not, responsible for sales/inventory management and training at the qualified distributor for the commercialization of Nuvyor (lenalidomide)."
                    )}
                </Typography>
            </Box>
        </Box>
    );

    return (
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} noValidate>
            <Grid container spacing={2}>
                {/* User Types (Multi-select) */}
                <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                        <Box sx={{ flex: 1 }}>
                            <Autocomplete
                                multiple
                                options={userTypeOptions}
                                getOptionLabel={(option) => option.label}
                                value={selectedTypeOptions}
                                onChange={(event, newValue) => {
                                    onChangeUserTypes(newValue.map(opt => opt.value));
                                }}
                                getOptionDisabled={(option) => {
                                    // If DISTRIBUTOR_RESPONSIBLE is selected, disable all other types
                                    if (hasDistributorResponsible) {
                                        return option.value !== USER_TYPE_VALUES.DISTRIBUTOR_RESPONSIBLE;
                                    }
                                    // Check for forbidden combinations
                                    if (isForbiddenCombination(userTypes, option.value)) {
                                        return true;
                                    }
                                    return false;
                                }}
                                isOptionEqualToValue={(option, value) => option.value === value.value}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            variant="outlined"
                                            label={option.label}
                                            {...getTagProps({ index })}
                                            key={option.value}
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={intl.get('UserTypes').d('User Types')}
                                        placeholder={intl.get('SelectUserTypes').d('Select one or more user types')}
                                        error={!!fieldErrors.userTypes}
                                        helperText={fieldErrors.userTypes}
                                    />
                                )}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                {hasDistributorResponsible
                                    ? intl.get('ExclusiveUserTypeHelp').d('Distributor Responsible cannot be combined with other types.')
                                    : includesPrescriber(userTypes)
                                        ? intl.get('PrescriberCombinationHelp').d('Prescriber cannot be combined with Dispenser or Institution Responsible.')
                                        : includesDispenser(userTypes)
                                            ? intl.get('DispenserCombinationHelp').d('Dispenser cannot be combined with Prescriber.')
                                            : includesInstitutionResponsible(userTypes)
                                                ? intl.get('InstitutionResponsibleCombinationHelp').d('Institution Responsible cannot be combined with Prescriber.')
                                                : intl.get('UserTypesHelp').d('You can select multiple types if applicable to your role.')}
                            </Typography>
                        </Box>
                        <Tooltip
                            title={userTypesInfoTooltip}
                            arrow
                            placement="right"
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        bgcolor: "background.paper",
                                        color: "text.primary",
                                        boxShadow: 3,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        "& .MuiTooltip-arrow": {
                                            color: "background.paper",
                                            "&::before": {
                                                border: "1px solid",
                                                borderColor: "divider",
                                            }
                                        }
                                    }
                                }
                            }}
                        >
                            <IconButton
                                size="small"
                                sx={{
                                    mt: 1,
                                    color: "info.main",
                                    "&:hover": {
                                        color: "info.dark",
                                        backgroundColor: "transparent"
                                    }
                                }}
                            >
                                <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Grid>

                {/* Institution */}
                <Grid item xs={12}>
                    <DmsInstitutionSelect
                        label={intl.get("InstitutionsLinkedTo").d("Institutions (linked to)")}
                        value={selectedInstitutions}
                        onChange={(value) => setSelectedInstitutions(value)}
                        multiple={true}
                        userTypes={userTypes}
                        category={institutionCategoryFilter}
                        needsAuthentication={false}
                        showAvailable={true}
                        customOptions={
                            createdInstitutions.map(inst => ({
                                value: inst.organization_branch_pk,
                                label: inst.name,
                            }))
                        }
                        fullWidth
                        disabled={userTypes.length === 0}
                        showCreateButton={true}
                        onCreateInstitution={() => setInstitutionModalVisible(true)}
                        onItemsLoaded={handleInstitutionsLoaded}
                        error={!!fieldErrors.selectedInstitutions}
                        helperText={fieldErrors.selectedInstitutions || intl.get("SelectInstitutionsYouWorkAt").d("Select all institutions you work at")}
                    />
                </Grid>

                {/* Responsible For (Institution Responsible only) */}
                {showResponsibleForField && (
                    <Grid item xs={12}>
                        <Autocomplete
                            multiple
                            fullWidth
                            disabled={selectedInstitutions.length === 0}
                            options={selectedInstitutions.filter(id => id !== "NO-INSTITUTION")}
                            value={responsibleForInstitutions}
                            onChange={(event, newValue) => setResponsibleForInstitutions(newValue)}
                            getOptionLabel={(option) => {
                                // Use institutionNamesMap to get the name
                                return institutionNamesMap[option] || `Institution ${option}`;
                            }}
                            isOptionEqualToValue={(option, value) => option === value}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={intl.get("ResponsibleFor").d("Responsible For (as Institution Responsible)")}
                                    error={!!fieldErrors.responsibleForInstitutions}
                                    helperText={
                                        fieldErrors.responsibleForInstitutions ||
                                        intl.get("SelectInstitutionsResponsibleFor").d("Which institutions are you responsible for? (Must be from the list above)")
                                    }
                                />
                            )}
                        />
                    </Grid>
                )}

                {/* Personal Info */}
                <Grid item xs={12}>
                    <DmsTextField
                        name="name"
                        label={intl.get("Name").d("Name")}
                        fullWidth
                        value={name}
                        onChange={(e) => onChangeName(e.target.value)}
                        error={!!fieldErrors.name}
                        helperText={fieldErrors.name || (showPrescriberFields ? intl.get("NameMustMatchCRM").d("We recommend using the same name as registered in the CRM") : "")}
                    />
                </Grid>

                <Grid item xs={12}>
                    <DmsTextFieldEmail
                        value={email}
                        fullWidth
                        disabled
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DmsTextFieldPhone
                        name="phone"
                        label={intl.get("PhoneNumber").d("Phone Number")}
                        fullWidth
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        error={!!fieldErrors.phone}
                        helperText={fieldErrors.phone}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <DmsTextFieldCpf
                        name="cpf"
                        label={intl.get("CPF").d("CPF")}
                        fullWidth
                        value={cpf}
                        onChange={(e) => setCpf(e.target.value)}
                        onValidationChange={(isValid) => setCpfIsValid(isValid)}
                        error={!!fieldErrors.cpf}
                        helperText={fieldErrors.cpf}
                    />
                </Grid>

                {/* Address Information */}
                <Grid item xs={12}>
                    <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
                        {intl.get("AddressInfo").d("Address Information")}
                        {showDispenserFields && !showPrescriberFields && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                ({intl.get("Optional").d("Optional")})
                            </Typography>
                        )}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <DmsTextFieldCep
                        name="zip_code"
                        label={intl.get("ZipCode").d("Zip Code")}
                        value={zipCode}
                        onChange={(e) => setZipCode(e.target.value)}
                        fullWidth
                        error={!!fieldErrors.zipCode}
                        helperText={fieldErrors.zipCode}
                    />
                </Grid>
                <Grid item xs={12} sm={8}>
                    <DmsTextField
                        name="address"
                        label={intl.get("Address").d("Address")}
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        fullWidth
                        error={!!fieldErrors.address}
                        helperText={fieldErrors.address}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <DmsTextField
                        name="address_number"
                        label={intl.get("Number").d("Number")}
                        value={addressNumber}
                        onChange={(e) => setAddressNumber(e.target.value)}
                        fullWidth
                        error={!!fieldErrors.addressNumber}
                        helperText={fieldErrors.addressNumber}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <DmsTextField
                        name="city"
                        label={intl.get("City").d("City")}
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        fullWidth
                        error={!!fieldErrors.city}
                        helperText={fieldErrors.city}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <DmsAutocomplete
                        options={stateOptions}
                        value={addressState}
                        label={intl.get("State").d("State")}
                        name="address_state"
                        fullWidth
                        onChange={(value) => setAddressState(value)}
                        error={!!fieldErrors.addressState}
                        helperText={fieldErrors.addressState}
                    />
                </Grid>

                {/* Professional Info - Prescriber (Doctor) */}
                {showPrescriberFields && (
                    <>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
                                {intl.get("PrescriberInfo").d("Prescriber Information")}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextFieldCrm
                                fullWidth
                                        value={crm}
                                onChange={(e) => onChangeCrm(e.target.value)}
                                error={!!fieldErrors.crm}
                                helperText={fieldErrors.crm}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <DmsAutocomplete
                                options={stateOptions}
                                value={crmState}
                                label={intl.get('CRMState').d('CRM State')}
                                name="crm_state"
                                fullWidth
                                        onChange={(value) => onChangeCrmState(value)}
                                error={!!fieldErrors.crmState}
                                helperText={fieldErrors.crmState}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Button
                                variant="contained"
                                color={isCrmValid ? "success" : "primary"}
                                onClick={handleCrmValidation}
                                disabled={!canValidateCrm || isValidatingCrm || isCrmValid}
                                fullWidth
                                sx={{ height: '56px' }}
                            >
                                {isValidatingCrm ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : isCrmValid ? (
                                    <>
                                        <CheckIcon sx={{ mr: 1 }} />
                                        {intl.get("Validated").d("Validated")}
                                    </>
                                ) : (
                                    intl.get("ValidateCRM").d("Validate CRM")
                                )}
                            </Button>
                        </Grid>
                        {crmValidationError && (
                            <Grid item xs={12}>
                                <Alert severity="error" sx={{ mt: 1 }}>
                                    {crmValidationError}
                                </Alert>
                            </Grid>
                        )}
                        {crmValidationWarning && (
                            <Grid item xs={12}>
                                <Alert severity="warning" sx={{ mt: 1 }}>
                                    { crmValidationWarning}
                                </Alert>
                            </Grid>
                        )}
                        {isCrmValid && (
                            <Grid item xs={12}>
                                <Alert severity="success" sx={{ mt: 1 }}>
                                    {intl.get("CRMValidatedSuccessfully").d("CRM validated successfully with CFM.")}
                                </Alert>
                            </Grid>
                        )}
                        {fieldErrors.crmValidation && (
                            <Grid item xs={12}>
                                <Alert severity="error" sx={{ mt: 1 }}>
                                    {fieldErrors.crmValidation}
                                </Alert>
                            </Grid>
                        )}
                        {/* Institution Link Declaration for Doctors */}
                        <Grid item xs={12}>
                            <Box sx={{
                                mt: 1,
                                p: 2,
                                bgcolor: fieldErrors.institutionLinkDeclaration ? 'error.lighter' : 'info.lighter',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: fieldErrors.institutionLinkDeclaration ? 'error.main' : 'info.main'
                            }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={institutionLinkDeclaration}
                                            onChange={(e) => setInstitutionLinkDeclaration(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Typography variant="body2">
                                            {intl.get("InstitutionLinkDeclaration").d("I declare that the information about my institutional link is true and accurate. I understand that false statements may result in the cancellation of my registration.")}
                                        </Typography>
                                    }
                                />
                                {fieldErrors.institutionLinkDeclaration && (
                                    <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                        {fieldErrors.institutionLinkDeclaration}
                                    </Typography>
                                )}
                            </Box>
                        </Grid>
                    </>
                )}

                {/* Professional Info - Dispenser (Pharmacist) */}
                {showDispenserFields && (
                    <>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
                                {intl.get("DispenserInfo").d("Dispenser Information")}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextFieldCrf
                                fullWidth
                                        value={crf}
                                onChange={(e) => setCrf(e.target.value)}
                                error={!!fieldErrors.crf}
                                helperText={fieldErrors.crf}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DmsAutocomplete
                                options={stateOptions}
                                value={crfState}
                                label={intl.get('CRFState').d('CRF State')}
                                name="crf_state"
                                fullWidth
                                onChange={(value) => setCrfState(value)}
                                error={!!fieldErrors.crfState}
                                helperText={fieldErrors.crfState}
                            />
                        </Grid>
                    </>
                )}

                {/* Password */}
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        id="password"
                        name="dms_password"
                        label={intl.get("Password").d("Password")}
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        variant="outlined"
                        error={!!fieldErrors.password}
                        helperText={fieldErrors.password}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <LockIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        id="confirmPassword"
                        name="confirmPassword"
                        label={intl.get("ConfirmPassword").d("Confirm Password")}
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        variant="outlined"
                        error={!!fieldErrors.confirmPassword}
                        helperText={fieldErrors.confirmPassword}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <LockIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <PasswordValidation
                        password={password}
                        confirmPassword={confirmPassword}
                    />
                </Grid>

                {/* Legal and Submit */}
                <Grid item xs={12}>
                    <Box sx={{
                        mt: 1,
                        p: 2,
                        bgcolor: fieldErrors.legalDocumentsAccepted ? 'error.lighter' : 'grey.50',
                        borderRadius: 1,
                        border: fieldErrors.legalDocumentsAccepted ? '1px solid' : 'none',
                        borderColor: fieldErrors.legalDocumentsAccepted ? 'error.main' : 'transparent'
                    }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={legalDocumentsAccepted}
                                    onChange={(e) => setLegalDocumentsAccepted(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    {intl.get("IHaveReadAndAcceptThe").d("I have read and accept the")}{" "}
                                    <Link
                                        component="button"
                                        type="button"
                                        onClick={() => handleOpenDocument('terms')}
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {intl.get("TermsOfUseAndPrivacyPolicy").d("Terms of Use and Privacy Policy")}
                                    </Link>
                                </Typography>
                            }
                        />
                        {fieldErrors.legalDocumentsAccepted && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', ml: 4 }}>
                                {fieldErrors.legalDocumentsAccepted}
                            </Typography>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={isSubmitting}
                            sx={{ minWidth: 200 }}
                        >
                            {isSubmitting ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                intl.get("Save").d("Save")
                            )}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
            {/* Institution Creation Modal */}
            <InstitutionSetupModal
                isVisible={institutionModalVisible}
                onClose={() => setInstitutionModalVisible(false)}
                onSave={handleInstitutionSave}
                mode="create"
                publicMode={true}
                userType={
                    // Determine primary user type for institution category filtering (priority order)
                    userTypes.includes(USER_TYPE_VALUES.DISTRIBUTOR_RESPONSIBLE) ? USER_TYPE_VALUES.DISTRIBUTOR_RESPONSIBLE :
                    userTypes.includes(USER_TYPE_VALUES.PRESCRIBER) ? USER_TYPE_VALUES.PRESCRIBER :
                    userTypes.includes(USER_TYPE_VALUES.DISPENSER) ? USER_TYPE_VALUES.DISPENSER :
                    userTypes.includes(USER_TYPE_VALUES.INSTITUTION_RESPONSIBLE) ? USER_TYPE_VALUES.INSTITUTION_RESPONSIBLE :
                    null
                }
            />
        </form>
    );
};
